import React from 'react';
import { useTranslation } from 'react-i18next';
import { getAgentAssets } from '../config/agentAssets';
import assistantI18n from '../i18n';

export interface AgentBannerProps {
  /** Agent kit name (e.g., 'payouts.assistant.payouts_info_agent') */
  agentKitName: string;
  /** Optional width override */
  width?: number | string;
  /** Optional height override */
  height?: number | string;
  /** Optional CSS class */
  className?: string;
  /** Optional alt text override */
  alt?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
}

/**
 * Agent Banner Component
 *
 * Replaces all individual banner components (PayoutsInfoBanner, InquiriesBanner, etc.)
 * Uses configuration-driven asset loading for maintainability.
 *
 * @example
 * <AgentBanner agentKitName="payouts.assistant.payouts_info_agent" />
 * <AgentBanner agentKitName="disputes.inquiries" width={400} />
 */
export const AgentBanner: React.FC<AgentBannerProps> = ({
  agentKitName,
  width,
  height,
  className,
  alt,
  style = {},
  ...props
}) => {
  const { t } = useTranslation('assistant', { i18n: assistantI18n });

  // Get asset configuration for this agent
  const assetConfig = getAgentAssets(agentKitName);

  if (!assetConfig) {
    console.error(`No asset configuration found for agent: ${agentKitName}`);
    return (
      <div
        className={className}
        style={{
          width: width || 320,
          height: height || 120,
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
          ...style,
        }}
      >
        No banner available
      </div>
    );
  }

  // Use provided dimensions or fall back to asset config defaults
  const finalWidth = width ?? assetConfig.defaultBannerSize?.width ?? 320;
  const finalHeight = height ?? assetConfig.defaultBannerSize?.height ?? 120;

  // Use provided alt text or translate from config
  const altText = alt || `${t(assetConfig.translationKey, assetConfig.altText)} Banner`;

  return (
    <img
      alt={altText}
      className={className}
      height={finalHeight}
      src={assetConfig.banner}
      style={{
        display: 'block',
        objectFit: 'cover',
        borderRadius: '8px',
        ...style,
      }}
      width={finalWidth}
      {...props}
    />
  );
};

export default AgentBanner;
